import { createContext, useContext, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ServiceNotificationBanner } from "@/components/ServiceNotificationBanner";
import { AuthBackdrop } from "@/components/AuthBackdrop";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { Footer } from "@/components/Footer";
import saleFishOnLight from "@/assets/logos/salefish-wordmark-on-light.png";
import saleFishOnDark from "@/assets/logos/salefish-wordmark-on-dark.png";
import { useTheme } from "@/components/ThemeProvider";

/**
 * The frame every sign-in screen shares — Login, Forgot/Reset Password, 2FA.
 *
 * PropPay's AuthLayout WITHOUT its photograph (Andrew, 2026-09-27: "remove
 * the images on the left. Use just the section on the right"). What was the
 * right-hand column is the whole page: the theme/language controls in a bar
 * at the top, then one 400px column holding the card — wordmark and greeting
 * inside it (AuthCard) — and PropPay's footer under it.
 *
 * Behind it all, the sign-in film (AuthBackdrop, Andrew 2026-10-01): the bars
 * are transparent so it runs edge to edge, and they wear the DARK theme's
 * tokens in both themes (`dark` on the header and footer) because they sit
 * on a dark film either way — only the card follows the reader's theme.
 * AuthBackdrop sets `html.auth-backdrop-on`, which paints the canvas and the
 * top-edge sample in the film's ink so the iOS status-bar strip matches it.
 */
const Heading = createContext<{ title: ReactNode; subtitle?: ReactNode } | null>(null);

export function AuthPageShell({
  title,
  subtitle,
  children,
}: {
  /** The page's one <h1> — drawn at the top of the card (AuthCard). */
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const { isVisible } = useMenuVisibility();
  const showLanguageToggle = isVisible("language_switcher");

  return (
    <Heading.Provider value={{ title, subtitle }}>
      <div className="auth-shell flex flex-col">
        <AuthBackdrop />
        {/* Only the controls up here now: the wordmark lives in the card, with
            the greeting (Andrew, 2026-09-28). Ghost icons, no outlines. */}
        {/* Sticky, not static: raising the keyboard on the email field makes
            iOS re-lay the web view to full height, and a static bar at the
            document's top then slides under the clock and is composited soft
            (standing rule; Mattamy 81e1f2d). A sticky one stops there.

            `top-0` with the top band as its own padding, so the toggles clear
            an iPadOS window's controls. TRANSPARENT, deliberately: the toggles
            float over the film (Andrew, 2026-10-03 — a solid ink bar made the
            screen feel heavy). No top-edge strip of its own — the root one
            (App.tsx) is the sample, in the film's ink. */}
        <header className="dark sticky top-0 z-chrome shrink-0 pt-[var(--sa-top)]">
          <div className="flex h-14 items-center justify-end gap-1 px-gutter sm:h-16">
          {showLanguageToggle && (
            <>
              <LanguageToggle />
              {/* A hairline between the two controls, so they read as two
                  separate choices rather than one pair. */}
              <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
            </>
          )}
          <ThemeToggle />
          </div>
        </header>

        {/* The service banner, under the bar, so the top of the screen is the
            bar's ink whatever the banner's colour. */}
        <ServiceNotificationBanner />

        {/* Tight on a phone so the whole screen fits above Safari's floating
            toolbar without a scroll (Andrew, 2026-09-28: "the footer touches the
            address bar"): measured on an iPhone, the visible band is ~728pt
            and the page was ~760. The first pass also cut every gap and read
            "too tight" (2026-09-29); the card keeps its full padding. Below md
            the footer sits on the bottom edge (Andrew, 2026-10-01), and the
            shell is 100svh there, not 100dvh — the SMALL viewport, measured
            with Safari's toolbars showing — so that edge is above the
            toolbar, not behind it (index.css `.auth-shell`). */}
        <main className="flex items-start justify-center px-5 pb-8 pt-3 sm:px-8 sm:pb-10 sm:pt-4 md:flex-1 md:items-center md:py-12">
          <div className="w-full max-w-[400px]">{children}</div>
        </main>

        <Footer className="dark bg-transparent" />
      </div>
    </Heading.Provider>
  );
}

/**
 * The sign-in card: the SaleFish wordmark, the page's greeting and its body,
 * all in one box (Andrew, 2026-09-28: "put the logo inside the login box").
 * Every auth screen uses it, so the four look like one product.
 */
export function AuthCard({ children, contentClassName }: { children: ReactNode; contentClassName?: string }) {
  const heading = useContext(Heading);
  // The coloured wordmark in the theme's own pair — deep violet on the light
  // card, lavender on the dark one (Andrew, 2026-09-28: back to the two
  // coloured ones). `effectiveTheme` is what ThemeProvider actually applied.
  const { effectiveTheme } = useTheme();
  return (
    <Card className="surface-elevated">
      <CardContent className={cn("p-6 sm:p-7", contentClassName)}>
        {heading && (
          /* The app's own header shape (Andrew, 2026-09-28): the bar edge that
             ends every header in the app, running the full width of the card —
             centred, with the title on the h2 rung of the type scale (its
             tracking comes with the rung) and the description under it. */
          <div className="-mx-6 -mt-6 mb-6 flex flex-col items-center gap-1.5 border-b border-border px-6 pb-5 pt-6 text-center sm:-mx-7 sm:-mt-7 sm:mb-7 sm:px-7 sm:pt-7">
            <img
              src={String(effectiveTheme === "dark" ? saleFishOnDark : saleFishOnLight)}
              alt="SaleFish"
              className="mb-4 h-6 w-auto"
              loading="eager"
              decoding="async"
              data-testid="img-logo"
            />
            <h1 className="text-h2 font-bold">{heading.title}</h1>
            {heading.subtitle ? <p className="text-pretty text-body text-muted-foreground">{heading.subtitle}</p> : null}
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}
